import { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import '../customization-page.css';
import { StoreState } from '../../../store/store';
import { CustomizationObjectType, Modality, ModalityDirection, ModelVisibility } from '../../../store/global-types/customization-types';
import { collectDicomAttributeIds, computeValidationStatus, filterTriggerFormErrors } from '../../../store/global-types/store-errors';
import CustomizationSection from '../CustomizationSection';
import CustomizationForm from '../CustomizationForm';
import { adaptSelectors, metadataItemRemoved, metadataItemUpdated, modelCustomizationDescriptionUpdated, modelVisibilityUpdated, metadataItemAdded, scrollToViewFromModelCustomizationMetadataRemoved, activeTagFilterToggled, activeModalityFilterToggled, activeVersionTagFilterToggled } from '../../../store/adapt/adaptSlice';
import { makeSelectIsAnyMetadataForOutputModified, makeSelectIsAnyTriggerForBaseModified } from '../../../store/adapt/adaptSelectors';
import AdaptSelectionRules from '../../selection/adapt/AdaptSelectionRules';
import AdaptOutputForm from './AdaptOutputForm';
import RoiSelectionForm from './RoiSelectionForm';
import { RTSTRUCT_METADATA_ATTRIBUTE_PRESETS } from '../../../store/adapt/adapt-types';

interface AdaptCustomizationFormProps {
    outputId: string,
}

const AdaptCustomizationForm = (props: AdaptCustomizationFormProps) => {
    const { outputId } = props;
    const dispatch = useDispatch();

    const { t } = useTranslation();

    const customizationOutput = useSelector((state: StoreState) => adaptSelectors.selectOutputById(state, outputId));
    const customizationBase = useSelector((state: StoreState) => customizationOutput ? adaptSelectors.selectCustomizationBaseById(state, customizationOutput.modelCustomizationBaseId) : undefined);
    const model = useSelector((state: StoreState) => customizationBase ? adaptSelectors.selectModelById(state, customizationBase.modelId) : undefined);
    const validationEntities = useSelector(adaptSelectors.selectCustomizationValidationErrorEntities);
    const formValidationErrors = useSelector(adaptSelectors.selectFormValidationErrors);
    const dicomRuleEntities = useSelector(adaptSelectors.selectDicomRuleEntities);
    const outputMetadata = useSelector(adaptSelectors.selectOutputMetadataEntities);
    const originalMetadata = useSelector(adaptSelectors.selectOriginalOutputMetadataEntities);
    
    // Get active filters for visual feedback
    const activeTags = useSelector(adaptSelectors.selectActiveTags);
    const activeVersionTags = useSelector(adaptSelectors.selectActiveVersionTags);
    const activeInputModalities = useSelector(adaptSelectors.selectActiveInputModalities);
    const activeOutputModalities = useSelector(adaptSelectors.selectActiveOutputModalities);
    const [isOutputSectionExpanded, setOutputSectionExpanded] = useState(true);
    const [isRoiRulesSectionExpanded, setRoiRulesSectionExpanded] = useState(true);

    const selectIsAnyMetadataForOutputModified = useMemo(makeSelectIsAnyMetadataForOutputModified, []);
    const isAnyMetadataModified = useSelector((state: StoreState) => selectIsAnyMetadataForOutputModified(state, outputId));

    const selectIsAnyTriggerRuleForBaseModified = useMemo(makeSelectIsAnyTriggerForBaseModified, []);
    const isAnyTriggerRuleModified = useSelector((state: StoreState) => customizationOutput ? selectIsAnyTriggerRuleForBaseModified(state, customizationOutput.modelCustomizationBaseId) : false);


    const handleToggleOutputSectionExpanded = useCallback(() => {
        setOutputSectionExpanded(!isOutputSectionExpanded);
    }, [isOutputSectionExpanded]);

    const handleToggleRoiRulesSectionExpanded = useCallback(() => {
        setRoiRulesSectionExpanded(!isRoiRulesSectionExpanded);
    }, [isRoiRulesSectionExpanded]);

    const onSetModelVisibility = useCallback((modelId: string, visibility: ModelVisibility) => {
        dispatch(modelVisibilityUpdated({ modelId, visibility }));
    }, [dispatch]);

    const onDescriptionChanged = useCallback((text: string) => {
        if (customizationBase) {
            dispatch(modelCustomizationDescriptionUpdated({ customizationBaseId: customizationBase.id, description: text }));
        }
    }, [dispatch, customizationBase]);

    const onMetadataItemUpdated = useCallback((metadataId: string, attribute: string | undefined, value: string | undefined, isUndoOperation?: boolean) => {
        dispatch(metadataItemUpdated({ metadataId, attribute, value, isUndoOperation }));
    }, [dispatch]);

    const handleAddMetadataEntry = useCallback((customizationOutputId: string) => {
        dispatch(metadataItemAdded(customizationOutputId));
    }, [dispatch]);

    const onMetadataItemRemoved = useCallback((metadataId: string) => {
        dispatch(metadataItemRemoved(metadataId));
    }, [dispatch]);

    const removeScrollToViewFromMetadataItem = useCallback((metadataId: string) => {
        dispatch(scrollToViewFromModelCustomizationMetadataRemoved(metadataId));
    }, [dispatch]);

    const handleToggleModelTag = useCallback((tag: string) => {
        dispatch(activeTagFilterToggled(tag));
    }, [dispatch]);

    const handleToggleModelModality = useCallback((modality: Modality, direction: ModalityDirection) => {
        dispatch(activeModalityFilterToggled({ modality, direction }));
    }, [dispatch]);

    const handleToggleModelVersionTag = useCallback((tag: string) => {
        dispatch(activeVersionTagFilterToggled(tag));
    }, [dispatch]);

    // sanity check that we have retrieved valid objects from redux store
    // (this check has to be done after all hooks calls)
    if (customizationOutput === undefined || customizationBase === undefined || model === undefined) {
        return null;
    }

    const outputHasBackendErrors = validationEntities[outputId]?.type === CustomizationObjectType.CustomizationOutput;
    const outputValidationStatus = computeValidationStatus(outputHasBackendErrors);

    const dicomAttributeIds = collectDicomAttributeIds(customizationBase.dicomRules, dicomRuleEntities);
    const triggerFormErrors = filterTriggerFormErrors(formValidationErrors, customizationBase.aeTitleRules, customizationBase.dicomRules, dicomAttributeIds);

    return (<CustomizationForm
        model={model}
        customizationBase={customizationBase}
        customizationOutput={customizationOutput}
        validationEntities={validationEntities}
        triggerFormValidationErrors={triggerFormErrors}
        outputMetadata={outputMetadata}
        originalMetadata={originalMetadata}
        metadataPresets={RTSTRUCT_METADATA_ATTRIBUTE_PRESETS}
        isAnyMetadataModified={isAnyMetadataModified}
        isAnyTriggerRuleModified={isAnyTriggerRuleModified}
        onModelTagToggled={handleToggleModelTag}
        onModelVersionTagToggled={handleToggleModelVersionTag}
        onModelModalityToggled={handleToggleModelModality}
        onSetModelVisibility={onSetModelVisibility}
        onAddMetadataEntry={handleAddMetadataEntry}
        onDescriptionChanged={onDescriptionChanged}
        onMetadataItemUpdated={onMetadataItemUpdated}
        onMetadaItemRemoved={onMetadataItemRemoved}
        removeScrollToViewFromMetadataItem={removeScrollToViewFromMetadataItem}
        isMetadataSectionDisabled={false}
        isDicomSelectionDisabled={false}
        hideTargetFile={true}
        activeTags={activeTags}
        activeVersionTags={activeVersionTags}
        activeInputModalities={activeInputModalities}
        activeOutputModalities={activeOutputModalities}
        selectionRules={(
            <AdaptSelectionRules
                customizationBaseId={customizationBase.id}
                isUndoDisabled={true}
                setUndoState={() => { }}
                heading={`model-selection-for-${customizationBase.id}`}
                hideCustomizationName
                showMultipleOutputsWarning={customizationBase.outputs.length > 1}
            />
        )}
    >

        <CustomizationSection
            label={t('customizationPage.adapt.outputSection.title')}
            isModified={customizationOutput.isModified}
            validationStatus={outputValidationStatus}
            collapsedMessage={t('customizationPage.adapt.outputSection.message')}
            isExpanded={isOutputSectionExpanded}
            onClick={handleToggleOutputSectionExpanded}
        >
            <AdaptOutputForm outputId={outputId} />
        </CustomizationSection>

        <CustomizationSection
            label={t('customizationPage.adapt.roiSelectionSection.title')}
            isModified={customizationOutput.isModified}
            validationStatus={outputValidationStatus}
            collapsedMessage={t('customizationPage.adapt.roiSelectionSection.message')}
            isExpanded={isRoiRulesSectionExpanded}
            onClick={handleToggleRoiRulesSectionExpanded}
            testId="structure-selection-section"
        >
            <RoiSelectionForm outputId={outputId} />
        </CustomizationSection>

    </CustomizationForm>
    );
}

export default AdaptCustomizationForm;
